import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useWebSocket, BackgroundTaskNotification } from '../contexts/WebSocketContext';
import { getNotificationCardBg, getNotificationCardBorder, getNotificationCardHeading, getNotificationCardText } from '../utils/statusColors';

export const BackgroundTaskNotifications: React.FC = () => {
  const { notifications, dismissNotification } = useWebSocket();
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  // Handle dismissal with animation
  const handleDismiss = (id: string) => {
    setDismissingIds(prev => new Set(prev).add(id));
    // Remove from state after animation completes
    setTimeout(() => {
      dismissNotification(id);
      setDismissingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300); // Match animation duration
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-3 max-w-sm">
      {notifications.map((notification, index) => (
        <NotificationCard
          key={notification.id}
          notification={notification}
          index={index}
          isDismissing={dismissingIds.has(notification.id)}
          onDismiss={() => handleDismiss(notification.id)}
        />
      ))}
    </div>
  );
};

interface NotificationCardProps {
  notification: BackgroundTaskNotification;
  index: number;
  isDismissing: boolean;
  onDismiss: () => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, index, isDismissing, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger slide-in animation after mount
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isDismissing) {
      setIsVisible(false);
    }
  }, [isDismissing]);
  const getIcon = () => {
    switch (notification.status) {
      case 'in_progress':
        return <Loader className="w-5 h-5 text-accent-500 animate-spin flex-shrink-0" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-error-500 flex-shrink-0" />;
      default:
        return <Loader className="w-5 h-5 text-gray-500 animate-spin flex-shrink-0" />;
    }
  };

  // Map notification status to notification type for consistent styling
  const getNotificationType = (): "info" | "success" | "warning" | "error" => {
    switch (notification.status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'in_progress':
      default:
        return 'info';
    }
  };

  const notificationType = getNotificationType();

  return (
    <div
      className={`${getNotificationCardBg(notificationType)} ${getNotificationCardBorder(notificationType)} border-l-4 border rounded-xl shadow-xl p-4 backdrop-blur-sm ${
        isVisible && !isDismissing
          ? 'animate-slide-up'
          : isDismissing
          ? 'animate-slide-down'
          : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded-lg ${
          notification.status === 'completed' 
            ? 'bg-success-100 dark:bg-success-900/30' 
            : notification.status === 'error'
            ? 'bg-error-100 dark:bg-error-900/30'
            : 'bg-accent-100 dark:bg-accent-900/30'
        }`}>
          {getIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className={`font-semibold text-sm ${getNotificationCardHeading(notificationType)}`}>
              {notification.title}
            </h4>
            <button
              onClick={onDismiss}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <p className={`text-xs ${getNotificationCardText(notificationType)} opacity-90`}>
            {notification.message}
          </p>
          
          {/* Link button */}
          {notification.link && (
            <a
              href={notification.link}
              className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = notification.link!;
              }}
            >
              {notification.linkText || 'View'}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          )}
          
          {/* Progress bar */}
          {notification.status === 'in_progress' && notification.progress !== undefined && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-accent-500 to-accent-600 dark:from-accent-600 dark:to-accent-700 transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${notification.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 font-medium">
                {notification.progress}% complete
              </p>
            </div>
          )}
          
          {/* Timestamp */}
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {formatTimestamp(notification.timestamp)}
          </p>
        </div>
      </div>
    </div>
  );
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 60) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
};
